'use client';

import { FormGroup } from '../../templates/form-group/form-group';
import type {
  SelectButtonContainerProps,
  SelectButtonOptionProps,
  SelectButtonProps,
} from './select-button.types';

function Option({
  label,
  value,
  onChange,
  onClick,
  inputId,
  disabled,
  name,
  required,
  inputProps,
}: SelectButtonOptionProps) {
  return (
    <label
      className="relative flex flex-1 group cursor-pointer items-center justify-center rounded-md bg-primary-background-50 p-8-8 transition-all hover:bg-secondary-variant-50 has-[:disabled]:pointer-events-none has-[:checked]:bg-secondary-variant-50"
      htmlFor={`${inputId}-${value}`}
    >
      <input
        {...inputProps}
        id={`${inputId}-${value}`}
        className="peer opacity-0 absolute !w-[1px] left-1/2 pointer-events-none"
        type="radio"
        onChange={onChange}
        onClick={onClick}
        name={name}
        value={value}
        disabled={disabled}
        required={required}
      />
      <span className="hc-typography--paragraph-lg font-semibold text-primary-900">
        {label}
      </span>
    </label>
  );
}

function Container({
  children,
  disabled,
  errorText,
  label,
  helpText,
  showErrors,
  className,
}: SelectButtonContainerProps) {
  return (
    <FormGroup className={className} disabled={disabled} error={showErrors}>
      <span className="hc-label">{label}</span>
      <div className="flex justify-stretch gap-4-4 rounded-lg border border-primary-900 p-4-4 transition-all hover:border-secondary-900 has-[:active]:scale-[0.98] has-[:checked]:border-secondary-variant-900">
        {children}
      </div>
      <span className="hc-help-text">{helpText}</span>
      <span className="hc-error-text">{errorText}</span>
    </FormGroup>
  );
}

export function SelectButton(properties: SelectButtonProps) {
  const {
    errorText,
    error,
    name,
    options,
    disabled,
    required,
    onChange,
    onClick,
    id,
  } = properties;
  const showErrors = error || !!errorText;
  const inputId = id || name;
  const containerProperties = { ...properties, showErrors };

  const { ContainerView, OptionView } = properties;
  const SelectContainer = ContainerView ?? Container;
  const SelectOption = OptionView ?? Option;

  return (
    <SelectContainer {...containerProperties}>
      {options.map(({ value, label: optionLabel, ...inputProps }) => (
        <SelectOption
          key={value}
          disabled={disabled}
          required={required}
          inputId={inputId}
          inputProps={inputProps}
          label={optionLabel}
          name={name}
          onChange={onChange ?? inputProps.onChange}
          onClick={onClick ?? inputProps.onClick}
          value={value}
        />
      ))}
    </SelectContainer>
  );
}
